import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { BackToDashboard } from "../components/BackToDashboard";
import {
  ChevronLeft, ChevronRight, CalendarDays, FileText,
  CalendarCheck, X, Building2, Clock, AlertTriangle,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

// ─── Calendar grid helpers ───────────────────────────────────────────────────

function buildGrid(year, month) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseToDate(raw) {
  if (!raw) return null;
  try {
    // ISO format
    const d = new Date(raw);
    if (!isNaN(d)) return d;
  } catch {}
  try {
    // MM/DD/YYYY
    const parts = raw.split("T")[0].split("/");
    if (parts.length === 3) return new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
  } catch {}
  return null;
}

// ─── Day detail modal ────────────────────────────────────────────────────────

function DayModal({ date, events, onClose }) {
  const label = date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const permits = events.filter(e => e.eventType === "permit");
  const inspections = events.filter(e => e.eventType === "inspection");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div
        className="bg-white rounded-sm border border-zinc-200 shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div>
            <div className="label-eyebrow">Day Detail</div>
            <div className="font-display font-bold text-lg tracking-tight mt-0.5">{label}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-sm">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Permits issued on this day */}
          {permits.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileText size={14} className="text-zinc-500" />
                <span className="label-eyebrow">Permits Issued</span>
              </div>
              <div className="space-y-2">
                {permits.map((p, i) => (
                  <div key={i} className="border border-zinc-200 rounded-sm px-4 py-3 bg-white">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-mono text-xs text-zinc-700">{p.permit_number}</span>
                      <StatusBadge status={p.status} />
                    </div>
                    <div className="text-sm font-medium mt-1">{p.permit_type}</div>
                    {p.job_nickname && (
                      <div className="text-xs text-zinc-500 mt-0.5">{p.job_nickname}</div>
                    )}
                    {p.address && (
                      <div className="flex items-center gap-1 text-xs text-zinc-400 mt-0.5">
                        <Building2 size={10} /> {p.address}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-4 mt-2 text-xs text-zinc-500">
                      {p.issuance_date && (
                        <span><span className="text-zinc-400">Issued:</span> {fmtDate(p.issuance_date)}</span>
                      )}
                      {p.expiration_date && (
                        <span><span className="text-zinc-400">Expires:</span> {fmtDate(p.expiration_date)}</span>
                      )}
                      {p.owner && (
                        <span><span className="text-zinc-400">Owner:</span> {p.owner}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inspections on this day */}
          {inspections.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <CalendarCheck size={14} className="text-zinc-500" />
                <span className="label-eyebrow">Inspections</span>
              </div>
              <div className="space-y-2">
                {inspections.map((insp, i) => (
                  <div key={i} className="border border-blue-200 bg-blue-50 rounded-sm px-4 py-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-blue-900">{insp.inspection_type}</span>
                      <StatusBadge status={insp.status} />
                    </div>
                    {insp.job_nickname && (
                      <div className="text-xs text-blue-700 mt-0.5">{insp.job_nickname}</div>
                    )}
                    {insp.address && (
                      <div className="flex items-center gap-1 text-xs text-blue-500 mt-0.5">
                        <Building2 size={10} /> {insp.address}
                      </div>
                    )}
                    {insp.permit_number && (
                      <div className="text-xs text-zinc-500 mt-1 font-mono">Permit #{insp.permit_number}</div>
                    )}
                    {insp.scheduled_at && (
                      <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1">
                        <Clock size={10} />
                        {new Date(insp.scheduled_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {permits.length === 0 && inspections.length === 0 && (
            <div className="text-center py-8 text-zinc-400 text-sm">
              No permits or inspections on this day.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Legend chip ─────────────────────────────────────────────────────────────

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${color}`} />
      {label}
    </span>
  );
}

// ─── Main Calendar Page ───────────────────────────────────────────────────────

export default function CalendarView() {
  const [permits, setPermits] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [jobs, setJobs] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  // Start on current month
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  useEffect(() => {
    const load = async () => {
      try {
        const [pr, ins, jb] = await Promise.all([
          api.get("/permits"),
          api.get("/inspections"),
          api.get("/jobs"),
        ]);
        const jobMap = {};
        (Array.isArray(jb.data) ? jb.data : []).forEach(j => { jobMap[j.job_id] = j; });
        setJobs(jobMap);
        setPermits(Array.isArray(pr.data) ? pr.data : []);
        setInspections(Array.isArray(ins.data) ? ins.data : []);
      } catch (e) {
        console.error("Calendar load failed:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Build a map of dateKey → events[]
  const eventsByDay = useMemo(() => {
    const m = {};
    permits.forEach(p => {
      const raw = p.issuance_date || p.filing_date;
      const d = parseToDate(raw);
      if (!d) return;
      const k = dateKey(d);
      if (!m[k]) m[k] = [];
      m[k].push({
        eventType: "permit",
        permit_number: p.permit_number,
        permit_type: p.permit_type,
        status: p.status,
        issuance_date: p.issuance_date,
        expiration_date: p.expiration_date,
        owner: p.owner,
        job_nickname: p.job_nickname,
        address: p.address,
      });
    });
    inspections.forEach(insp => {
      const d = parseToDate(insp.scheduled_at);
      if (!d) return;
      const k = dateKey(d);
      if (!m[k]) m[k] = [];
      m[k].push({
        eventType: "inspection",
        inspection_type: insp.inspection_type,
        status: insp.status,
        scheduled_at: insp.scheduled_at,
        job_nickname: insp.job_nickname,
        address: insp.address,
        permit_number: insp.permit_number,
      });
    });
    return m;
  }, [permits, inspections]);

  const grid = useMemo(() => buildGrid(year, month), [year, month]);

  const shift = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 0) { newMonth = 11; newYear -= 1; }
    if (newMonth > 11) { newMonth = 0; newYear += 1; }
    setMonth(newMonth);
    setYear(newYear);
  };

  const monthLabel = new Date(year, month, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  const todayKey = dateKey(today);

  const selectedEvents = selectedDay ? (eventsByDay[dateKey(selectedDay)] || []) : [];

  // Count stats for the current month
  const monthStats = useMemo(() => {
    let permitCount = 0;
    let inspCount = 0;
    grid.forEach(d => {
      if (!d) return;
      const k = dateKey(d);
      const evts = eventsByDay[k] || [];
      permitCount += evts.filter(e => e.eventType === "permit").length;
      inspCount += evts.filter(e => e.eventType === "inspection").length;
    });
    return { permitCount, inspCount };
  }, [grid, eventsByDay]);

  return (
    <div className="px-4 sm:px-8 py-8 max-w-7xl mx-auto" data-testid="calendar-page">
      <BackToDashboard />
      <span className="label-eyebrow">Schedule</span>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-1">Calendar</h1>
      <p className="text-sm text-zinc-500 mb-6">Permit issuance dates and scheduled inspections — click any day to see details.</p>

      {/* Month summary pills */}
      {!loading && (
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-sm text-sm">
            <FileText size={13} className="text-amber-600" />
            <span className="font-semibold text-amber-800">{monthStats.permitCount}</span>
            <span className="text-amber-700">permit{monthStats.permitCount !== 1 ? "s" : ""} this month</span>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-sm text-sm">
            <CalendarCheck size={13} className="text-blue-600" />
            <span className="font-semibold text-blue-800">{monthStats.inspCount}</span>
            <span className="text-blue-700">inspection{monthStats.inspCount !== 1 ? "s" : ""} this month</span>
          </div>
        </div>
      )}

      {/* Calendar card */}
      <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
        {/* Month navigation */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-zinc-50">
          <button
            onClick={() => shift(-1)}
            className="p-1.5 hover:bg-zinc-200 rounded-sm transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-3">
            <CalendarDays size={16} className="text-zinc-400" />
            <span className="font-display font-bold tracking-tight text-base">{monthLabel}</span>
          </div>
          <button
            onClick={() => shift(1)}
            className="p-1.5 hover:bg-zinc-200 rounded-sm transition-colors"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
            <div key={d} className="px-2 py-2 label-eyebrow text-center border-r border-zinc-100 last:border-r-0">{d}</div>
          ))}
        </div>

        {/* Grid cells */}
        {loading ? (
          <div className="grid grid-cols-7">
            {[...Array(35)].map((_, i) => (
              <div key={i} className="min-h-[90px] border-r border-b border-zinc-100 p-2">
                <Skeleton className="h-3 w-4 mb-2" />
                <Skeleton className="h-4 w-full mb-1" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {grid.map((d, i) => {
              const k = d ? dateKey(d) : null;
              const dayEvents = k ? (eventsByDay[k] || []) : [];
              const dayPermits = dayEvents.filter(e => e.eventType === "permit");
              const dayInsps = dayEvents.filter(e => e.eventType === "inspection");
              const isToday = k === todayKey;
              const isSelected = selectedDay && k === dateKey(selectedDay);
              const hasEvents = dayEvents.length > 0;

              return (
                <div
                  key={i}
                  onClick={() => d && setSelectedDay(d)}
                  className={`min-h-[90px] sm:min-h-[110px] border-r border-b border-zinc-100 p-1.5 transition-colors
                    ${d ? "cursor-pointer" : "bg-zinc-50/60"}
                    ${isSelected ? "bg-amber-50 border-amber-200 ring-1 ring-amber-300 ring-inset" : ""}
                    ${hasEvents && !isSelected ? "hover:bg-zinc-50" : ""}
                  `}
                >
                  {d && (
                    <>
                      <div className={`text-xs font-mono w-6 h-6 flex items-center justify-center rounded-full
                        ${isToday ? "bg-amber-500 text-white font-bold" : "text-zinc-500"}`}>
                        {d.getDate()}
                      </div>
                      <div className="space-y-0.5 mt-1">
                        {/* Permit events */}
                        {dayPermits.slice(0, 2).map((p, pi) => (
                          <div
                            key={pi}
                            className="text-[10px] truncate px-1 py-0.5 bg-amber-50 text-amber-900 rounded-sm border border-amber-200 leading-tight"
                            title={`${p.permit_type} — ${p.permit_number}`}
                          >
                            <FileText size={8} className="inline mr-0.5 -mt-0.5" />
                            {p.permit_type || p.permit_number}
                          </div>
                        ))}
                        {/* Inspection events */}
                        {dayInsps.slice(0, 2).map((insp, ii) => (
                          <div
                            key={ii}
                            className="text-[10px] truncate px-1 py-0.5 bg-blue-50 text-blue-900 rounded-sm border border-blue-200 leading-tight"
                            title={`${insp.inspection_type} — ${insp.job_nickname}`}
                          >
                            <CalendarCheck size={8} className="inline mr-0.5 -mt-0.5" />
                            {insp.inspection_type}
                          </div>
                        ))}
                        {/* Overflow indicator */}
                        {dayEvents.length > 4 && (
                          <div className="text-[10px] text-zinc-500 px-1">+{dayEvents.length - 4} more</div>
                        )}
                        {dayPermits.length > 2 && dayInsps.length === 0 && (
                          <div className="text-[10px] text-zinc-500 px-1">+{dayPermits.length - 2} more</div>
                        )}
                        {dayInsps.length > 2 && dayPermits.length === 0 && (
                          <div className="text-[10px] text-zinc-500 px-1">+{dayInsps.length - 2} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-3 px-1">
        <LegendDot color="bg-amber-200 border border-amber-300" label="Permit issuance date" />
        <LegendDot color="bg-blue-200 border border-blue-300" label="Scheduled inspection" />
        <LegendDot color="bg-amber-500" label="Today" />
      </div>

      {/* Day detail modal */}
      {selectedDay && (
        <DayModal
          date={selectedDay}
          events={selectedEvents}
          onClose={() => setSelectedDay(null)}
        />
      )}
    </div>
  );
}
